<template>
  <v-row class="justify-center" density="compact">
    <v-sheet class="button-group" v-if="meetingStore.isDraw">
      <v-btn class="button" v-bind="buttonProps" prepend-icon="msf:videocam" @click="dismissSnapshot" id="resumeCall">
        {{ $t('meeting.toolbar.resume') }}
      </v-btn>
      <div class="divider"></div>
      <v-btn class="button" v-bind="buttonProps" prepend-icon="msf:save" @click="saveSnapshot" id="saveSnapshot">
        {{ $t('meeting.toolbar.save') }}
      </v-btn>
    </v-sheet>
    <v-sheet class="button-group">
      <v-btn
        class="button"
        v-bind="buttonProps"
        prepend-icon="msf:arrow_selector_tool"
        @click="toggleSelect"
        :active="board.task === 'selecting'"
      >
        {{ $t('meeting.toolbar.select') }}
      </v-btn>
      <v-btn
        class="button"
        v-bind="buttonProps"
        prepend-icon="msf:north_east"
        @click="toggleDart"
        :active="board.task === 'darting'"
      >
        {{ $t('meeting.toolbar.point') }}
      </v-btn>
      <template v-if="!meetingStore.isDraw">
        <v-btn class="button" v-bind="buttonProps" prepend-icon="msf:photo_camera" @click="takeSnapshot">
          {{ $t('meeting.toolbar.snapshot') }}
        </v-btn>
        <v-btn class="button" v-bind="buttonProps" @click="toggleRecord">
          <template v-slot:prepend>
            <v-icon :class="{recording: recordingStore.isOn}" icon="msf:radio_button_checked"></v-icon>
          </template>
          {{ $t('meeting.toolbar.record') }}
        </v-btn>
        <v-menu :offset="[10, 0]" location="top center">
          <template v-slot:activator="{props}">
            <v-btn class="button" v-bind="{...buttonProps, ...props}" :readonly="isScreenSharing" :stacked="false">
              <div class="stacked-button">
                <v-icon icon="msf:present_to_all" />
                {{ $t('meeting.toolbar.share') }}
              </div>
              <v-icon icon="msf:keyboard_arrow_down" />
            </v-btn>
          </template>
          <v-sheet class="share" :rounded="8">
            <v-icon-btn icon="msf:upload_file" @click="useEventBus('droparea:signal:image:upload').emit()" />
            <v-icon-btn icon="msf:computer_arrow_up" @click="toggleScreensharing" />
          </v-sheet>
        </v-menu>
      </template>
      <template v-if="meetingStore.isDraw">
        <v-btn
          class="button"
          v-bind="buttonProps"
          prepend-icon="msf:stylus_note"
          @click="onTogglePen"
          :active="penActive"
        >
          {{ $t('meeting.toolbar.pen') }}
        </v-btn>
        <v-menu :offset="[10, 0]" location="top center">
          <template v-slot:activator="{props}">
            <v-btn
              class="button"
              v-bind="{...buttonProps, ...props}"
              :stacked="false"
              :readonly="isScreenSharing"
              :active="circleActive || rectActive"
            >
              <div class="stacked-button">
                <v-icon :icon="shapeButtonIcon" />
                {{ $t('meeting.toolbar.shapes') }}
              </div>
              <v-icon icon="msf:keyboard_arrow_down" />
            </v-btn>
          </template>
          <v-sheet class="share" :rounded="8">
            <v-btn v-bind="buttonProps" prepend-icon="circle" @click="onToggleCircle" :active="circleActive" />
            <v-btn v-bind="buttonProps" prepend-icon="crop_square" @click="onToggleRectangle" :active="rectActive" />
          </v-sheet>
        </v-menu>
        <v-btn
          class="button"
          v-bind="buttonProps"
          prepend-icon="msf:format_shapes"
          @click="onToggleText"
          :active="textActive"
        >
          {{ $t('meeting.toolbar.text') }}
        </v-btn>
        <v-btn class="button" v-bind="buttonProps" prepend-icon="msf:frame_inspect" @click="onToggleOCR">{{
          $t('meeting.toolbar.ocr')
        }}</v-btn>
        <template v-if="meetingStore.isDrawSelect">
          <div class="divider"></div>
          <v-menu :offset="[10, 0]" location="top center" v-model="weightOpened">
            <template v-slot:activator="{props}">
              <v-btn
                class="button"
                v-bind="{...buttonProps, ...props}"
                :readonly="isScreenSharing"
                :stacked="false"
                active-color="default"
                :active="weightOpened"
              >
                <div class="stacked-button">
                  <v-icon icon="line_weight" />
                  {{ $t('meeting.toolbar.weight') }}
                </div>
                <v-icon icon="msf:keyboard_arrow_down" />
              </v-btn>
            </template>
            <v-sheet class="share" :rounded="8">
              <v-btn
                v-for="size in board.sizes"
                variant="flat"
                :active="board.size === size"
                class="thickness"
                @click="onSelectThickness(size)"
              >
                <div :style="{height: `${size}px`}"></div>
              </v-btn>
            </v-sheet>
          </v-menu>
          <v-menu :offset="[10, 0]" location="top center">
            <template v-slot:activator="{props}">
              <v-btn
                class="button"
                v-bind="{...buttonProps, ...props}"
                :readonly="isScreenSharing"
                :active="(board.shape === 'circle' || board.shape === 'rect') && board.task === 'shaping'"
                active-color="default"
                :stacked="false"
              >
                <div class="stacked-button">
                  <v-icon icon="msf:crop_square" :color="board.color"></v-icon>
                  {{ $t('meeting.toolbar.color') }}
                </div>
                <v-icon icon="msf:keyboard_arrow_down" />
              </v-btn>
            </template>
            <v-sheet class="share" :rounded="8">
              <v-btn
                v-for="color in board.colors"
                variant="flat"
                :active="board.color === color"
                class="thickness"
                @click="onSelectColor(color)"
              >
                <v-icon icon="msf:crop_square" :color="color" variant="flat" />
              </v-btn>
            </v-sheet>
          </v-menu>
        </template>
      </template>
    </v-sheet>
    <v-sheet class="button-group" v-if="isScreenSharing">
      <v-btn class="button" v-bind="buttonProps" prepend-icon="stop_screen_share" @click="toggleScreensharing">
        {{ $t('meeting.toolbar.stopShare') }}
      </v-btn>
    </v-sheet>
    <v-sheet class="button-group" v-if="meetingStore.isDraw && meetingStore.isDrawSelect">
      <v-btn class="button" v-bind="buttonProps" prepend-icon="msf:delete" @click="removeShape" id="resumeCall">
        {{ $t('meeting.toolbar.remove') }}
      </v-btn>
    </v-sheet>
    <v-sheet class="button-group" v-if="recordingStore.isOn">
      <v-btn class="button" v-bind="buttonProps" prepend-icon="msf:rectangle" @click="stopRecording" id="stopRecording">
        {{ $t('meeting.toolbar.stopRecording') }}
      </v-btn>
    </v-sheet>
  </v-row>
</template>
<script setup>
import {fabric} from 'fabric'
import {useBoardStore, useBoardCanvasStore, useSnapshotStore} from '@/stores'
import {useMeetingStore} from '@/stores'
import {useRecordingStore} from '@/stores'
import {useSnapshot} from '@/composables/useSnapshot'
import {useBoardExport} from '@/composables/useBoardExport'
import {useEventBus} from '@vueuse/core'
import {useRecording} from '@/procedures/useRecording'
import {useAwsService} from '@/composables'
import {useTranslation} from 'i18next-vue'
import {ref, watchEffect} from 'vue'
import {computed} from 'vue'
import {useMessageStore} from '@/stores/messagesStore'
import {useScreenShareStore} from '@/stores/screenshareStore'
import {storeToRefs} from 'pinia'
const messageStore = useMessageStore()
const meetingStore = useMeetingStore()
const recordingStore = useRecordingStore()
const board = useBoardStore()
const canvasStore = useBoardCanvasStore()
const {take, dismiss} = useSnapshot()
const {uploadedImage, saveUploadedImage} = useSnapshot()
const {saveBoard} = useBoardExport()
const {callTextract} = useAwsService()
const snapshot = useSnapshotStore()
const screenShareStore = useScreenShareStore()
const {stop, start} = screenShareStore
const {isScreenSharing} = storeToRefs(screenShareStore)
const {t} = useTranslation()
const weightOpened = ref(false)
const buttonProps = {
  stacked: true,
  density: 'compact',
  size: 'small',
  variant: 'flat',
  rounded: 4,
  'active-color': 'primary',
}
const circleActive = computed(() => board.shape === 'circle' && board.task === 'shaping')
const rectActive = computed(() => board.shape === 'rect' && board.task === 'shaping')
const shapeButtonIcon = computed(() => {
  if (circleActive.value) {
    return 'circle'
  } else if (rectActive.value) {
    return 'crop_square'
  }
  return 'shapes'
})
const penActive = computed(() => board.shape === 'freedraw' && board.task === null)
const textActive = computed(() => board.shape === 'text' && board.task === 'typing')
watchEffect(() => {
  if (!meetingStore.isDrawSelect) {
    weightOpened.value = false
  }
})
const toggleSelect = () => {
  if (board.task === 'selecting') {
    board.setTask(null)
  } else {
    board.setTask('selecting')
  }
}
const toggleDart = () => {
  if (board.task === 'darting') {
    board.setTask(null)
  } else {
    board.setTask('darting')
  }
}
const stopRecording = () => {
  useRecording().stop()
}
const toggleRecord = () => {
  const isRecording = recordingStore.isOn
  if (isRecording) {
    useRecording().stop()
  } else {
    useRecording().start()
  }
}
const toggleScreensharing = () => {
  if (isScreenSharing.value) {
    stop()
  } else {
    start()
  }
}
const saveSnapshot = () => {
  useEventBus('board:signal:snapshot:save').emit()
  dismiss()
}
const dismissSnapshot = () => {
  // TODO handle when recording
  // useEventBus('board:signal:snapshot:save').emit()
  dismiss()
}
const takeSnapshot = async () => {
  take()
}
const onTogglePen = () => {
  if (penActive.value) {
    board.setTask(null)
    board.setShape(null)
  } else {
    board.setTask(null)
    board.setShape('freedraw')
  }
}
const onToggleCircle = () => {
  if (circleActive.value) {
    board.setTask(null)
    board.setShape(null)
  } else {
    board.setTask('shaping')
    board.setShape('circle')
  }
}
const onToggleRectangle = () => {
  if (rectActive.value) {
    board.setTask(null)
    board.setShape(null)
  } else {
    board.setTask('shaping')
    board.setShape('rect')
  }
}
const onToggleText = () => {
  if (textActive.value) {
    board.setTask(null)
    board.setShape(null)
  } else {
    board.setTask('typing')
    board.setShape('text')
  }
}
const onToggleOCR = async () => {
  if (board.ocr) {
    useEventBus('board:signal').emit('ocr:discard')
    return
  }
  try {
    messageStore.addMessage({
      text: t('meeting.notification.ocrAnalysing'),
    })
    if (uploadedImage.value) {
      // OCR the uploaded image itself: the boxes below are mapped onto the image, which a board export
      // (letterbox bars, zoom) does not match
      await saveUploadedImage()
    } else if (snapshot.takePictureTouch.isUpload) {
      await saveBoard()
    }
    const response = await callTextract(snapshot.key)
    response.forEach(item => {
      const {text} = item
      const selected = false
      const {left, top, width, height} = item.geometry.boundingBox
      const {
        scale,
        left: snapshotLeft,
        top: snapshotTop,
        width: snapshotWidth,
        height: snapshotHeight,
      } = snapshot.transform
      const rect = new fabric.Rect()
      rect.initOcrField({
        left: left * snapshotWidth * scale + snapshotLeft,
        top: top * snapshotHeight * scale + snapshotTop,
        width: width * snapshotWidth * scale,
        height: height * snapshotHeight * scale,
        text,
        selected,
      })
      canvasStore.addOcrField(rect)
      canvasStore.canvas.add(rect)
    })
    board.setOcr(true)
    board.setTask('selecting')
    messageStore.addMessage({
      text: t('meeting.notification.ocrFound', {lines: response.length}),
    })
    console.info('OCR fields', {response})
  } catch (error) {
    messageStore.addMessage({
      text: t('meeting.notification.ocrNotFound'),
    })
    console.error('Error recognizing OCR', error)
  }
}
const onSelectThickness = value => {
  board.size = value
}
const onSelectColor = value => {
  board.color = value
}
const removeShape = () => {
  useEventBus('board:shape:removed').emit(canvasStore.selection)
}
</script>
<style scoped lang="scss">
@import '@/scss/button-group.scss';
.recording {
  color: #d83737;
  animation: 1s pulse linear infinite;
}
.share {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: $spacing-xs;
  justify-content: center;
  align-items: center;
}
.thickness {
  padding-right: 4px;
  padding-left: 4px;

  div {
    width: 60px;
    background: #282828;
  }
}
.color {
  width: 30px;
  height: 30px;
}
.stacked-button {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
</style>
